/**
 * What `OnlineDriver` is allowed to know about the socket — the interfaces it
 * consumes, plus the one pure translation it needs. Split from `OnlineDriver.ts`
 * for the 200-line cap, and it splits cleanly: nothing here has behaviour that
 * depends on the state of a table.
 *
 * These are the *consumer's* interfaces, not the controller's exports:
 * `NetworkGameController` satisfies them structurally, so the driver can be
 * unit-tested with a plain object and never imports the network layer's class
 * or (via the overlay surface) the DOM.
 */

import type { Claim } from '../game/Claims.js';
import type { MahjongState } from '../game/stateCore.js';
import type { Seat } from '../game/types.js';
import type { KongDecision } from '../ai/types.js';
import type { MahjongClaimIntent, MahjongSnapshot } from '../network/protocol.js';
import type { OverlayView } from '../network/overlayState.js';

export type OnlineStateListener = (state: MahjongState, snapshot: MahjongSnapshot) => void;

/**
 * The connection half of the transport: everything that is not a mahjong
 * intent. `NetworkGameController` implements all of it.
 */
export interface MahjongOverlayTransport {
  /** Subscribe to overlay changes. Called back immediately with the current view. */
  onOverlayChange(listener: (view: OverlayView) => void): void;
  requestRematch(): void;
  acceptRematch(): void;
  declineRematch(): void;
  dismissOverlay(): void;
}

/** The slice of `NetworkGameController` the driver uses. */
export interface MahjongTransport {
  readonly localSeat: Seat | null;
  /** Optional so a fake exercising only intents stays a valid transport. */
  readonly overlay?: MahjongOverlayTransport;
  attach(): void;
  detach(): void;
  sendDiscard(tileId: number): void;
  sendClaim(claim: MahjongClaimIntent): void;
  sendPass(): void;
  sendTsumo(): void;
  sendKong(kong: KongDecision): void;
  sendNextHand(): void;
}

/** The DOM surface the overlay is painted on — `ui/OnlineOverlayHost.ts`. */
export interface OnlineOverlaySurface {
  update(view: OverlayView): void;
  dispose(): void;
}

/** Wire form of a local claim — the server derives the seat from the connection. */
export function claimIntent(claim: Claim): MahjongClaimIntent {
  switch (claim.type) {
    case 'ron':
      return { kind: 'ron' };
    case 'pung':
      return { kind: 'pung' };
    case 'chi':
      return { kind: 'chi', tileIds: [claim.option.tiles[0].id, claim.option.tiles[1].id] };
  }
}
